/**
 * Phase 25 (Plan 25-01) — LSR-06 page-mount permission gate.
 *
 * Sibling to RequireRole.tsx — RequireRole checks `user.roles`; RequirePermission
 * checks the auth-context `permissions` array (populated by the login response per
 * Phase 6 Plan 06-02 — see `LoginApiResponse.permissions`).
 *
 * NOTE on AuthContext shape: `useAuth()` exposes `permissions` as a top-level field
 * of AuthState (NOT nested under `user`). The plan's <interfaces> sketch assumed
 * `user.permissions` — actual production shape is `useAuth().permissions`. This is
 * the LIVE contract enforced by Phase 6 Plan 06-02 (login response keeps
 * permissions deduped + separate from user metadata so the same `AuthUser` row can
 * be persisted across role-permission changes without rewriting the user storage
 * blob). No AuthContext modification required.
 *
 * Defense-in-depth only — the backend `[HasPermission(...)]` attributes
 * (Plan 25-02 / Plan 25-03) are the authoritative gate. Bypassing this wrapper
 * by editing JS in DevTools still results in a 403 from the API.
 *
 * Usage:
 *   <Route
 *     path="/reports/logsheet"
 *     element={
 *       <RequirePermission permission="reports.logsheet.view">
 *         <LogsheetReportPage />
 *       </RequirePermission>
 *     }
 *   />
 */

import type { JSX, ReactNode } from 'react';
import { Navigate } from 'react-router-dom';

import { useAuth } from '@/lib/auth/AuthContext';

export interface RequirePermissionProps {
  readonly permission: string;
  readonly children: ReactNode;
  readonly fallbackTo?: string;
}

export function RequirePermission({
  permission,
  children,
  fallbackTo = '/dashboard',
}: RequirePermissionProps): JSX.Element {
  const { permissions } = useAuth();
  if (!permissions.includes(permission)) {
    return <Navigate to={fallbackTo} replace />;
  }
  return <>{children}</>;
}
